import { useCallback, useEffect, useRef, useState } from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import { useMatchesViewport } from '../hooks/media';
import { dispatchTrackingEventGA4 } from '../tracking/dispatchTrackingEventGA4';
import { getSafeEventHandlerOpts } from '../utils/touchEvent';

const MIN_DESKTOP_HEIGHT = 400;
const MIN_MOBILE_HEIGHT = 300;
const TRACKING_EVENT_NAME = 'ScrollToTop_button_click';
const TOUCH_EVENT_OPTIONS = getSafeEventHandlerOpts({
  capture: true,
  passive: false,
});

let bodyOverflowHiddenRequests = 0;

const trackEvent = () => {
  dispatchTrackingEventGA4({
    event: TRACKING_EVENT_NAME,
    element: 'button',
    button_text: 'scroll to top',
  });
};

const resetBodyOverflowHidden = () => {
  if (bodyOverflowHiddenRequests > 0) {
    bodyOverflowHiddenRequests--;
    if (bodyOverflowHiddenRequests === 0) {
      document.body.style.removeProperty('overflow');
    }
  }
};

const applyBodyOverflowHidden = () => {
  document.body.style.overflow = 'hidden';
  bodyOverflowHiddenRequests++;
};

export const useHiddenBodyOverflow = (shouldLockScroll: boolean) => {
  const requestedBodyOverflowHidden = useRef(false);

  useEffect(() => {
    if (shouldLockScroll) {
      requestedBodyOverflowHidden.current = true;
      applyBodyOverflowHidden();
    }

    return () => {
      if (requestedBodyOverflowHidden.current) {
        requestedBodyOverflowHidden.current = false;
        resetBodyOverflowHidden();
      }
    };
  }, [shouldLockScroll]);
};

const toggleBodyScroll = (shouldLockScroll: boolean, targetElement: Element) => {
  if (shouldLockScroll) {
    disableBodyScroll(targetElement);
  } else {
    enableBodyScroll(targetElement);
  }
};

export const useDisableBodyScroll = (shouldLockScroll: boolean, targetElementSelector: string) =>
  useEffect(() => {
    const targetElement = document.querySelector(targetElementSelector);

    if (targetElement) {
      toggleBodyScroll(shouldLockScroll, targetElement);
    }
  }, [shouldLockScroll, targetElementSelector]);

export const useTrackScrollPosition = (ref: HTMLElement) => {
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });

  const onScroll = useCallback(event => {
    const { scrollTop: y, scrollLeft: x } = event.target;

    setScrollPosition({ x, y });
  }, []);

  useEffect(() => {
    if (ref) {
      ref.addEventListener('scroll', onScroll, TOUCH_EVENT_OPTIONS);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', onScroll, TOUCH_EVENT_OPTIONS);
      }
    };
  }, [ref]);

  return scrollPosition;
};

export const useScrollToTop = () => {
  const [isButtonVisible, setIsButtonVisible] = useState(false);
  const {
    matches: { matchesDesktop },
  } = useMatchesViewport();
  const minHeight = matchesDesktop ? MIN_DESKTOP_HEIGHT : MIN_MOBILE_HEIGHT;

  useEffect(() => {
    const onScroll = () => {
      setIsButtonVisible(window.scrollY > minHeight);
    };
    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, [minHeight]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    trackEvent();
  };

  return { scrollToTop, isButtonVisible };
};
